import clsx from 'clsx';
import { GridProps } from '../grid-old/grid.d';
import { getChildSpans } from '../helpers';
import { children, createMemo, createUniqueId } from 'solid-js';
import { ErrorCatcher } from '../../tools/error-catcher';
import theme from '../../style/theme';

const SIDE_GUTTERS = 3;

export const Grid = (props: GridProps) => {
    const spans = createMemo(() => getChildSpans({ templateShorthand: props.templateShorthand }));
    const resolved = children(() => props.children);
    const childArray = createMemo(() => resolved.toArray());

    const totalCols = createMemo(() => {
        const spanArray = spans();
        if (spanArray.length === 0) return theme.grid.columns;
        return spanArray.reduce((total, count) => total + count, 0);
    });

    const responsiveColumns = createMemo(() => {
        const responsive = props.responsive;
        if (!responsive) return null;

        const desktopSpans = responsive.smallDesktop || responsive.largeDesktop || spans();
        const tabletSpans = responsive.tablet || responsive.smallDesktop || responsive.largeDesktop || spans();
        const mobileSpans = responsive.mobile || responsive.tablet || responsive.smallDesktop || responsive.largeDesktop || spans();

        return {
            desktop: desktopSpans.reduce((total, count) => total + count, 0),
            tablet: tabletSpans.reduce((total, count) => total + count, 0),
            mobile: mobileSpans.reduce((total, count) => total + count, 0),
        };
    });

    const gridClasses = createMemo(() => clsx(
        'atos-grid',
        'grid-base',
        {
            'grid-base-with-layout': props.isLayoutGrid,
            'grid-base-without-layout': !props.isLayoutGrid,
            'grid-base-row-gap-single': spans().length <= 1,
            'grid-base-row-gap-multiple': spans().length > 1,
        },
        // Desktop responsive - always applied (old code always generates desktopCss)
        'grid-responsive-desktop',
        props.isLayoutGrid ? 'grid-responsive-desktop-with-layout' : 'grid-responsive-desktop-without-layout',
        
        // Tablet responsive - always applied
        'grid-responsive-tablet',
        props.isLayoutGrid ? 'grid-responsive-tablet-with-layout' : 'grid-responsive-tablet-without-layout',
        // Mobile responsive - always applied
        'grid-responsive-mobile',
        !props.isLayoutGrid && !props.autoResponsiveness && 'grid-responsive-mobile-custom'
    ));

    const gridInlineStyle = createMemo(() => {
        const declarations: string[] = [];

        // Set base column count
        const columns = props.gridColumns || totalCols();
        declarations.push(`--grid-columns: ${columns}`);

        // Set responsive column counts - always set these, using base columns as fallback
        const respCols = responsiveColumns();
        if (respCols) {
            declarations.push(`--grid-columns-responsive: ${respCols.desktop}`);
            declarations.push(`--grid-columns-tablet: ${respCols.tablet}`);
            declarations.push(`--grid-columns-mobile: ${respCols.mobile}`);
        } else {
            // No responsive prop - use base column count for all breakpoints
            declarations.push(`--grid-columns-responsive: ${columns}`);
            declarations.push(`--grid-columns-tablet: ${columns}`);
            declarations.push(`--grid-columns-mobile: ${columns}`);
        }

        // Custom CSS from props
        const customCss = props.customCss?.trim();
        if (customCss) {
            declarations.push(customCss);
        }

        return declarations.join('; ');
    });

    // Generate unique ID for this grid instance (SSR-safe)
    const generatedId = createUniqueId();
    const gridUniqueId = createMemo(() => `grid-${generatedId}`);

    // Generate dynamic CSS for child positioning - works during SSR
    // This generates breakpoint-specific rules to match the old styled-components behavior
    const dynamicChildPositionCss = createMemo(() => {
        const baseSpans = spans();
        const childCount = childArray().length;
        const responsive = props.responsive;

        if (childCount === 0) return '';

        const rules: string[] = [];
        const gridId = gridUniqueId();

        // Helper to generate nth-child rules for a set of spans
        const generateChildRules = (spanArray: number[], start: number) => {
            const childRules: string[] = [];
            for (let i = 0; i < childCount; i++) {
                const span = spanArray[i % spanArray.length];
                childRules.push(`
                    #${gridId} > *:nth-child(${i + 1}) {
                        ${i === 0 ? `grid-column-start: ${start};` : ''}
                        grid-column-end: span ${span};
                    }
                `);
            }
            return childRules.join('\n');
        };

        // 1. Base (desktop large) rules - no media query
        const baseStart = props.isLayoutGrid ? SIDE_GUTTERS : 1;
        rules.push(generateChildRules(baseSpans, baseStart));

        // 2. Desktop responsive (1270px) - use responsive.smallDesktop or responsive.largeDesktop
        const desktopSpans = responsive?.smallDesktop || responsive?.largeDesktop || baseSpans;
        const desktopStart = props.isLayoutGrid ? 2 : 1; // At 1270px, layout grids use column 2
        rules.push(`
            @container (max-width: 1270px) {
                ${generateChildRules(desktopSpans, desktopStart)}
            }
        `);

        // 3. Tablet responsive (600px)
        // Key behavior: if isLayoutGrid OR autoResponsiveness, force all children to span 12
        const autoResp = props.autoResponsiveness !== false; // default is true
        const tabletSpans = (props.isLayoutGrid || autoResp)
            ? [12]
            : (responsive?.tablet || responsive?.smallDesktop || responsive?.largeDesktop || baseSpans);
        const tabletStart = props.isLayoutGrid ? 2 : 1;
        rules.push(`
            @container (max-width: 600px) {
                ${generateChildRules(tabletSpans, tabletStart)}
            }
        `);

        // 4. Mobile responsive (480px)
        // Only apply custom spans if NOT isLayoutGrid AND NOT autoResponsiveness
        if (!props.isLayoutGrid && !autoResp) {
            const mobileSpans = responsive?.mobile || responsive?.tablet || responsive?.smallDesktop || responsive?.largeDesktop || baseSpans;
            const mobileStart = 1;
            rules.push(`
                @media only screen and (max-width: 480px) {
                    ${generateChildRules(mobileSpans, mobileStart)}
                }
            `);
        }

        return rules.join('\n');
    });

    return (
        <ErrorCatcher componentName='Grid'>
            
            <div
                id={gridUniqueId()}
                class={gridClasses()}
                style={gridInlineStyle()}
                data-layout-grid={props.isLayoutGrid ? 'true' : 'false'}
                data-auto-responsiveness={props.autoResponsiveness !== false ? 'true' : 'false'}
            >
                {resolved()}
            </div>
            <style>{dynamicChildPositionCss()}</style>
        </ErrorCatcher>
    );
};
